<template>
    <div>
        <v-form ref="modifyForm" @submit="$event.preventDefault()">
            <div class="flex-grow-1 flex-equal d-flex align-center">
                <v-text-field
                        color="primary"
                        v-model="value.name"
                        label="Account Name"
                        outlined
                        dense
                        :rules="[validationRules.required]"
                        hide-details="auto"
                        @input="changesMade = true"
                >
                </v-text-field>
            </div>
            <div class="flex-grow-1 pt-4 flex-equal d-flex align-center">
                <v-text-field
                        color="primary"
                        outlined
                        v-model="value.username"
                        dense
                        label="Username"
                        placeholder="eg. person@domain.com"
                        hide-details="auto"
                        :hint="usernameErrors"
                        :error-messages="usernameErrors !== null ? [usernameErrors] : null"
                        @input="changesMade = true; usernameErrors = null"
                        :rules="[validationRules.required]"
                >
                </v-text-field>
            </div>
            <div class="flex-grow-1 pt-4 flex-equal d-flex align-center justify-space-between">
                <v-text-field
                        ref="password"
                        color="primary"
                        outlined
                        v-model="value.password"
                        dense
                        label="Password"
                        type="password"
                        placeholder="Password"
                        hide-details="auto"
                        autocomplete="new-password"
                        :rules="value.password ? [validationRules.required, validationRules.upperCase, validationRules.lowerCase, validationRules.numeric, validationRules.specialCharacter, validationRules.min6Characters] : []"
                        @input="changesMade = true"
                >
                </v-text-field>
                <v-tooltip left>
                    <template v-slot:activator="{on}">
                        <v-btn
                                :disabled="sendingPasswordReset"
                                :loading="sendingPasswordReset"
                                icon
                                text
                                color="primary"
                                class="mx-2"
                                @click="sendPasswordReset"
                                v-on="on"
                        >
                            <v-icon>mdi-send-lock</v-icon>
                        </v-btn>
                    </template>
                    Send Password Reset Mail
                </v-tooltip>
            </div>
            <div class="flex-grow-1 pt-2 flex-equal d-flex align-baseline justify-space-between">
                <v-switch
                        :loading="savingChanges"
                        color="primary"
                        v-model="value.totp.enabled"
                        label="Enable TOTP Login"
                        hide-details
                        @change="changesMade = true"
                ></v-switch>
                <v-tooltip left>
                    <template v-slot:activator="{on}">
                        <v-btn
                                :disabled="resettingTotp || !user.totp.enabled"
                                :loading="resettingTotp"
                                icon
                                text
                                color="primary"
                                class="mx-2"
                                @click="resetTotp"
                                v-on="on"
                        >
                            <v-icon>mdi-lock-reset</v-icon>
                        </v-btn>
                    </template>
                    Reset TOTP
                </v-tooltip>
            </div>
            <div class="flex-grow-1 pt-2 flex-equal d-flex align-baseline justify-space-between">
                <v-switch
                        :loading="savingChanges"
                        color="primary"
                        v-model="userDisabled"
                        label="Disable User"
                        hide-details
                        @change="changesMade = true"
                ></v-switch>
            </div>
            <div class="flex-grow-1 pt-4 flex-equal d-flex align-baseline justify-end">
                <v-btn
                        ref="passwordBtn"
                        :disabled="savingChanges"
                        color="primary"
                        @click="resetValue"
                        small
                        outlined
                        class="mr-2"
                >
                    Cancel
                </v-btn>
                <v-btn
                        ref="passwordBtn"
                        :disabled="!changesMade || savingChanges"
                        :loading="savingChanges"
                        color="primary"
                        @click="saveChanges"
                        small
                >
                    Save Changes
                </v-btn>
            </div>
        </v-form>
    </div>
</template>

<script>
import {arrSplice, validationRules} from "@/core/codeFunctions";
import {format} from "date-fns"
import {mapGetters} from "vuex";
import {User} from "@/models/user";

export default {
    props: {
        user: User
    },
    data() {
        return {
            con: console,
            val: null,
            format: format,
            value: new User({}),
            validationRules: validationRules,
            savingChanges: false,
            changesMade: false,
            usernameErrors: null,
            resettingTotp: false,
            sendingPasswordReset: false,
        }
    },
    watch: {
        user: {
            immediate: true,
            deep: true,
            handler(val) {
                if (val) {
                    this.resetValue()
                }
            }
        }
    },
    methods: {
        resetValue() {
            this.value = new User(JSON.parse(JSON.stringify(this.user)));
            if (this.$refs.modifyForm)
                this.$refs.modifyForm.resetValidation();
            this.usernameErrors = null
            this.savingChanges = false;
            this.changesMade = false;
            this.resettingTotp = false;
            this.sendingPasswordReset = false;
        },

        async sendPasswordReset() {
            try {
                this.sendingPasswordReset = true;
                const res = await this.$newReq('POST', 'users/passwordResetInvite', {_id: this.value._id})
                this.sendingPasswordReset = false;
                this.$snack.info('Password reset invite sent')
            } catch (e) {
                console.log(e)
                this.sendingPasswordReset = false;
            }
        },

        async resetTotp() {
            try {
                this.resettingTotp = true;
                const res = await this.$newReq('POST', 'users/resetTotp', {_id: this.value._id})
                this.resettingTotp = false;
                this.$snack.info('TOTP reset')
            } catch (e) {
                console.log(e)
                this.resettingTotp = false;
            }
        },

        async saveChanges() {
            try {

                if (!this.$refs.modifyForm.validate())
                    return this.$snack.error('Please Complete All Fields')

                // Check Username Duplicate
                const {duplicates} = await this.$newReq('POST', 'users/checkUsernameModify', {_id: this.value._id, username: this.value.username})
                if (duplicates)
                    this.usernameErrors = 'Username Already Taken'

                // Build The Update Object
                const updateKeys = ['name', 'username', 'totp', 'disabledSiteIds'];
                if (this.value.password)
                    updateKeys.push("password")

                this.savingChanges = true;
                this.user.once('update', (payload) => {this.savingChanges = false;})

                await this.value.update(...updateKeys)
            } catch (e) {
                console.log(e)
                this.savingChanges = false;
            }
        },
    },
    computed: {
        ...mapGetters(['users']),
        userDisabled: {
            get() {
                return this.value.disabledSiteIds.includes(this.$store.state.curSiteId)
            },
            set(val) {
                if (val)
                    this.value.disabledSiteIds.push(this.$store.state.curSiteId)
                else
                    arrSplice(this.value.disabledSiteIds, (id) => id === this.$store.state.curSiteId)
            }
        }
    }
}
</script>

<style scoped>

</style>